import { isPayPalCardType } from './cardTypes';
import { nullSafeToString } from '../lib/util';

/**
 * A payment method saved in the user's Braintree vault, from `GET /user/{userId}/paymentMethods`.
 *
 * The same listing carries saved cards and vaulted PayPal accounts; {@link SavedPaymentMethod#isPayPal
 * isPayPal} distinguishes them, and it determines which payment type a checkout must be saved with.
 * Using one requires no Braintree JavaScript at all - pass {@link SavedPaymentMethod#token token} to
 * {@link Collaterate.Checkout.setPayment Collaterate.Checkout.setPayment}.
 *
 * @property {string} token - (Read only) The vault token, used to pay with this method.
 * @property {boolean} isPayPal - (Read only) Whether this is a PayPal account rather than a card.
 * @property {string} cardType - (Read only) The Braintree card type, e.g. "Visa" or "PayPal".
 * @property {string} name - (Read only) The cardholder name, or the account email for PayPal.
 * @property {string} last4 - (Read only) The last four digits of the card. Empty for PayPal.
 * @property {string} maskedNumber - (Read only) The masked card number. Empty for PayPal.
 * @property {string} expirationDate - (Read only) The card expiration as "MM/YYYY". Empty for PayPal.
 * @property {boolean} isExpired - (Read only) Whether the card has expired.
 * @property {boolean} isDefault - (Read only) Whether this is the user's default payment method. Change it with {@link Collaterate.Checkout.updatePaymentMethod updatePaymentMethod}, which works for cards but not for PayPal accounts.
 * @property {string} imageUrl - (Read only) A card-brand or PayPal image URL suitable for display.
 * @property {string} postalCode - (Read only) The billing postal code on file, when present.
 */

class SavedPaymentMethod {
    constructor (data = {}) {
        this.token = nullSafeToString(data.token);
        this.cardType = nullSafeToString(data.cardType);
        this.isPayPal = isPayPalCardType(data.cardType);
        this.name = nullSafeToString(data.cardholderName);
        this.last4 = nullSafeToString(data.last4);
        this.maskedNumber = nullSafeToString(data.maskedNumber);
        this.expirationDate = nullSafeToString(data.expirationDate);
        this.isExpired = Boolean(data.isExpired);
        this.isDefault = Boolean(data.isDefault);
        this.imageUrl = nullSafeToString(data.imageUrl);
        this.postalCode = nullSafeToString(data.postalCode);

        Object.freeze(this);
    }
}

export default SavedPaymentMethod;
